import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import "./Workspace.css";

// Bootstrap Icons
import { 
  Buildings, 
  Folder, 
  PlusCircle,
  Gear,
  Clock,
  Person,
  Calendar,
  ArrowRepeat,
  ListTask,
  Diagram3,
  Collection,
  JournalText,
  Check2Square,
  Flag,
  Lightbulb,
  Activity,
  CalendarEvent,
  ClockHistory
} from "react-bootstrap-icons";

import { 
  fetchWorkspaces, 
  fetchProjects, 
  fetchTasks,
  createWorkspace,
  createProject,
  createTask 
} from "../../api/api";
import "./Workspace.css";

const Workspace = () => {
    const { user } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState("workspaces");
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [refreshFlag, setRefreshFlag] = useState(0);

    // Form states
    const [workspaceForm, setWorkspaceForm] = useState({ name: "", description: "" });
    const [projectForm, setProjectForm] = useState({ name: "", description: "" });
    const [taskForm, setTaskForm] = useState({ 
      description: "", 
      assignedTo: "", 
      status: "pending", 
      priority: "medium" 
    });
    
    // Loading states
    const [loading, setLoading] = useState({
      workspaces: false,
      projects: false,
      tasks: false,
      create: false
    });
    
    // Load workspaces
    useEffect(() => {
      if (!user?.id) return;
    
      const loadWorkspaces = async () => {
        setLoading(prev => ({ ...prev, workspaces: true }));
        try {
          const data = await fetchWorkspaces(user.id);
          setWorkspaces(data || []);
          if (data?.length && !selectedWorkspace) {
            setSelectedWorkspace(data[0].id);
          }
        } catch (err) {
          console.error("Failed to load workspaces:", err);
        } finally {
          setLoading(prev => ({ ...prev, workspaces: false }));
        }
      };
    
      loadWorkspaces();
    }, [user, refreshFlag]);
    
    // Load projects when workspace changes
    useEffect(() => {
      if (!selectedWorkspace) return;
    
      const loadProjects = async () => {
        setLoading(prev => ({ ...prev, projects: true }));
        try {
          const data = await fetchProjects(selectedWorkspace);
          setProjects(data.projects || []);
          if (data?.projects?.length && !selectedProject) {
            setSelectedProject(data.projects[0].id);
          } else {
            setSelectedProject(null);
          }
        } catch (err) {
          console.error("Failed to load projects:", err);
        } finally {
          setLoading(prev => ({ ...prev, projects: false }));
        }
      };
    
      loadProjects();
    }, [selectedWorkspace, refreshFlag]);
    
    // Load tasks when project changes
    useEffect(() => {
      if (!selectedProject) return;
    
      const loadTasks = async () => {
        setLoading(prev => ({ ...prev, tasks: true }));
        try {
          const data = await fetchTasks(selectedProject);
          setTasks(data.tasks || []);
        } catch (err) {
          console.error("Failed to load tasks:", err);
        } finally {
          setLoading(prev => ({ ...prev, tasks: false }));
        }
      };
    
      loadTasks();
    }, [selectedProject, refreshFlag]);
    
    // Handlers
    const handleCreateWorkspace = async (e) => {
      e.preventDefault();
      if (!workspaceForm.name) {
        alert("Workspace name is required");
        return;
      }
    
      setLoading(prev => ({ ...prev, create: true }));
      try {
        await createWorkspace(workspaceForm.name, user.id);
        setWorkspaceForm({ name: "", description: "" });
        setRefreshFlag(prev => prev + 1);
      } catch (err) {
        console.error(err);
        alert("Failed to create workspace");
      } finally {
        setLoading(prev => ({ ...prev, create: false }));
      }
    };
    
    const handleCreateProject = async (e) => {
      e.preventDefault();
      if (!projectForm.name) {
        alert("Project name is required");
        return;
      }
    
      setLoading(prev => ({ ...prev, create: true }));
      try {
        await createProject(selectedWorkspace, projectForm.name, projectForm.description);
        setProjectForm({ name: "", description: "" });
        setRefreshFlag(prev => prev + 1);
      } catch (err) {
        console.error(err);
        alert("Failed to create project");
      } finally {
        setLoading(prev => ({ ...prev, create: false }));
      }
    };
    
    const handleCreateTask = async (e) => {
      e.preventDefault();
      if (!taskForm.description) {
        alert("Task description is required");
        return;
      }
    
      setLoading(prev => ({ ...prev, create: true }));
      try {
        await createTask(
          selectedProject,
          taskForm.description,
          taskForm.assignedTo ? Number(taskForm.assignedTo) : null,
          taskForm.status,
          taskForm.priority
        );
        setTaskForm({ 
          description: "", 
          assignedTo: "", 
          status: "pending", 
          priority: "medium" 
        });
        setRefreshFlag(prev => prev + 1);
      } catch (err) {
        console.error(err);
        alert("Failed to create task");
      } finally {
        setLoading(prev => ({ ...prev, create: false }));
      }
    };
    
    const getSelectedWorkspaceName = () => {
      const ws = workspaces.find(w => w.id === selectedWorkspace);
      return ws ? ws.name : "Select a workspace";
    };
    
    const getSelectedProjectName = () => {
      const p = projects.find(p => p.id === selectedProject);
      return p ? p.name : "Select a project";
    };

    return (
      <div className="workspace-container">
        <header className="workspace-header">
          <div className="header-content">
            <h1>
              <Buildings size={32} className="text-primary" />
              Workspace Management
            </h1>
            <p className="subtitle">
              Organize workspaces, projects, and tasks efficiently
            </p>
          </div>
        </header>

        <div className="workspace-layout">
          {/* Left Sidebar */}
          <aside className="workspace-sidebar">
            <div className="sidebar-section">
              <h3>
                <Diagram3 size={16} className="me-2" />
                Navigation
              </h3>
              <nav className="nav-tabs">
                <button 
                  className={`nav-tab ${activeTab === "workspaces" ? "active" : ""}`}
                  onClick={() => setActiveTab("workspaces")}
                >
                  <span className="tab-icon"><Buildings size={18} /></span>
                  <span>Workspaces</span>
                  {workspaces.length > 0 && (
                    <span className="tab-count">{workspaces.length}</span>
                  )}
                </button>
                <button 
                  className={`nav-tab ${activeTab === "projects" ? "active" : ""}`}
                  onClick={() => setActiveTab("projects")}
                >
                  <span className="tab-icon"><Folder size={18} /></span>
                  <span>Projects</span>
                  {projects.length > 0 && (
                    <span className="tab-count">{projects.length}</span>
                  )}
                </button>
                <button 
                  className={`nav-tab ${activeTab === "tasks" ? "active" : ""}`}
                  onClick={() => setActiveTab("tasks")}
                >
                  <span className="tab-icon"><Check2Square size={18} /></span>
                  <span>Tasks</span>
                  {tasks.length > 0 && (
                    <span className="tab-count">{tasks.length}</span>
                  )}
                </button>
              </nav>
            </div>

            <div className="sidebar-section">
              <h3>
                <Gear size={16} className="me-2" />
                Quick Selection
              </h3>
              <div className="selection-card">
                <div className="selection-item">
                  <label>Workspace</label>
                  <select 
                    className="form-select modern-select"
                    value={selectedWorkspace || ""}
                    onChange={(e) => setSelectedWorkspace(Number(e.target.value))}
                    disabled={loading.workspaces}
                  >
                    <option value="">Select workspace</option>
                    {workspaces.map(ws => (
                      <option key={ws.id} value={ws.id}>
                        {ws.name}
                      </option>
                    ))}
                  </select>
                  {selectedWorkspace && (
                    <div className="selection-info">
                      <small>
                        {workspaces.find(w => w.id === selectedWorkspace)?.description || "No description"}
                      </small>
                    </div>
                  )}
                </div>

                <div className="selection-item">
                  <label>Project</label>
                  <select 
                    className="form-select modern-select"
                    value={selectedProject || ""}
                    onChange={(e) => setSelectedProject(Number(e.target.value))}
                    disabled={!selectedWorkspace || loading.projects}
                  >
                    <option value="">Select project</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {selectedProject && (
                    <div className="selection-info">
                      <small>
                        {projects.find(p => p.id === selectedProject)?.description || "No description"}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="sidebar-section">
              <h3>
                <Activity size={16} className="me-2" />
                Quick Stats
              </h3>
              <div className="quick-stats">
                <div className="stat-item">
                  <span className="stat-label">
                    <Buildings size={14} className="me-1" />
                    Active Workspaces
                  </span>
                  <span className="stat-value">{workspaces.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">
                    <Folder size={14} className="me-1" />
                    Total Projects
                  </span>
                  <span className="stat-value">{projects.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">
                    <ClockHistory size={14} className="me-1" />
                    Pending Tasks
                  </span>
                  <span className="stat-value">
                    {tasks.filter(t => t.status === 'pending').length}
                  </span>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="workspace-main">
            {/* Workspaces Tab */}
            {activeTab === "workspaces" && (
              <div className="tab-content">
                <div className="section-header">
                  <div>
                    <h2>
                      <Buildings size={24} className="me-2" />
                      Manage Workspaces
                    </h2>
                    <p>Create and organize your workspaces</p>
                  </div>
                  <button className="btn-refresh">
                    <ArrowRepeat size={16} className="me-1" />
                    Refresh
                  </button>
                </div>

                <div className="form-section">
                  <div className="form-header">
                    <h3>
                      <PlusCircle size={20} className="me-2" />
                      Create New Workspace
                    </h3>
                    <span className="form-badge">
                      Required fields marked with *
                    </span>
                  </div>
                  <form onSubmit={handleCreateWorkspace} className="modern-form">
                    <div className="form-group">
                      <label>
                        Workspace Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={workspaceForm.name}
                        onChange={(e) => setWorkspaceForm({...workspaceForm, name: e.target.value})}
                        placeholder="Enter workspace name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        className="form-textarea"
                        value={workspaceForm.description}
                        onChange={(e) => setWorkspaceForm({...workspaceForm, description: e.target.value})}
                        placeholder="Optional description"
                        rows="3"
                      />
                    </div>
                    <div className="form-actions">
                      <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={loading.create}
                      >
                        {loading.create ? (
                          <>
                            <span className="spinner"></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <PlusCircle size={16} className="me-1" />
                            Create Workspace
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="list-section">
                  <div className="list-header">
                    <h3>
                      <Collection size={20} className="me-2" />
                      Your Workspaces
                    </h3>
                    <span className="list-count">{workspaces.length} workspace(s)</span>
                  </div>
                  {loading.workspaces ? (
                    <div className="loading-state">
                      <span className="spinner"></span>
                      Loading workspaces...
                    </div>
                  ) : workspaces.length === 0 ? (
                    <div className="empty-state">
                      <Buildings size={48} className="empty-icon" />
                      <p>No workspaces found. Create your first one!</p>
                    </div>
                  ) : (
                    <div className="card-grid">
                      {workspaces.map(ws => (
                        <div 
                          key={ws.id} 
                          className={`workspace-card ${selectedWorkspace === ws.id ? "selected" : ""}`}
                          onClick={() => setSelectedWorkspace(ws.id)}
                        >
                          <div className="card-header">
                            <div className="card-title">
                              <span className="card-icon">
                                <Buildings size={24} className="text-primary" />
                              </span>
                              <h4>{ws.name}</h4>
                            </div>
                            <span className="card-badge">
                              Workspace
                            </span>
                          </div>
                          <p className="card-description">
                            {ws.description || "No description provided"}
                          </p>
                          <div className="card-footer">
                            <div className="footer-left">
                              <small>
                                <Calendar size={12} className="me-1" />
                                Created: {new Date(ws.created_at).toLocaleDateString()}
                              </small>
                            </div>
                            <div className="footer-right">
                              <span className="project-count">
                                <Folder size={12} className="me-1" />
                                {ws.project_count || 0} projects
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === "projects" && (
              <div className="tab-content">
                <div className="section-header">
                  <div className="header-left">
                    <h2>
                      <Folder size={24} className="me-2" />
                      Project Management
                    </h2>
                    <p>
                      Current workspace: <strong>{getSelectedWorkspaceName()}</strong>
                    </p>
                  </div>
                  {selectedWorkspace && (
                    <div className="header-right">
                      <span className="active-badge">
                        <Activity size={12} className="me-1" />
                        {projects.filter(p => p.status === 'active').length} active
                      </span>
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-header">
                    <h3>
                      <PlusCircle size={20} className="me-2" />
                      Create New Project
                    </h3>
                    <span className="form-badge">
                      <Lightbulb size={12} className="me-1" />
                      Select a workspace first
                    </span>
                  </div>
                  <form onSubmit={handleCreateProject} className="modern-form">
                    <div className="form-group">
                      <label>
                        Project Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={projectForm.name}
                        onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                        placeholder="Enter project name"
                        required
                        disabled={!selectedWorkspace}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        className="form-textarea"
                        value={projectForm.description}
                        onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                        placeholder="Optional description"
                        rows="3"
                        disabled={!selectedWorkspace}
                      />
                    </div>
                    <div className="form-actions">
                      <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={!selectedWorkspace || loading.create}
                      >
                        {loading.create ? (
                          <>
                            <span className="spinner"></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <PlusCircle size={16} className="me-1" />
                            Create Project
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="list-section">
                  <div className="list-header">
                    <h3>
                      <JournalText size={20} className="me-2" />
                      Projects in {getSelectedWorkspaceName()}
                    </h3>
                    <span className="list-count">{projects.length} project(s)</span>
                  </div>
                  {!selectedWorkspace ? (
                    <div className="empty-state">
                      <Folder size={48} className="empty-icon" />
                      <p>Please select a workspace first</p>
                    </div>
                  ) : loading.projects ? (
                    <div className="loading-state">
                      <span className="spinner"></span>
                      Loading projects...
                    </div>
                  ) : projects.length === 0 ? (
                    <div className="empty-state">
                      <Folder size={48} className="empty-icon" />
                      <p>No projects found in this workspace. Create the first one!</p>
                    </div>
                  ) : (
                    <div className="table-container">
                      <table className="modern-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Status</th>
                            <th>Tasks</th>
                            <th>Last Updated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projects.map(p => (
                            <tr 
                              key={p.id}
                              className={selectedProject === p.id ? "selected-row" : ""}
                              onClick={() => setSelectedProject(p.id)}
                            >
                              <td>
                                <div className="project-name">
                                  <span className="project-icon">
                                    <Folder size={20} className="text-primary" />
                                  </span>
                                  <div className="project-name-text">
                                    <div className="project-title">{p.name}</div>
                                    <small className="project-id">ID: {p.id}</small>
                                  </div>
                                </div>
                              </td>
                              <td className="project-description">
                                {p.description || <span className="no-description">-</span>}
                              </td>
                              <td>
                                <span className={`status-badge ${p.status || "active"}`}>
                                  {p.status || "active"}
                                </span>
                              </td>
                              <td>
                                <span className="task-count">
                                  <Check2Square size={12} className="me-1" />
                                  {p.task_count || 0} tasks
                                </span>
                              </td>
                              <td>
                                <span className="date-cell">
                                  <CalendarEvent size={12} className="me-1" />
                                  {new Date(p.updated_at || p.created_at).toLocaleDateString()}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === "tasks" && (
              <div className="tab-content">
                <div className="section-header">
                  <div className="header-left">
                    <h2>
                      <Check2Square size={24} className="me-2" />
                      Task Management
                    </h2>
                    <p>
                      Workspace: <strong>{getSelectedWorkspaceName()}</strong> • 
                      Project: <strong>{getSelectedProjectName()}</strong>
                    </p>
                  </div>
                  {selectedProject && (
                    <div className="header-right">
                      <span className="active-badge">
                        <ClockHistory size={12} className="me-1" />
                        {tasks.filter(t => t.status === 'pending').length} pending
                      </span>
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-header">
                    <h3>
                      <PlusCircle size={20} className="me-2" />
                      Create New Task
                    </h3>
                    <span className="form-badge">
                      <Lightbulb size={12} className="me-1" />
                      Select a project first
                    </span>
                  </div>
                  <form onSubmit={handleCreateTask} className="modern-form">
                    <div className="form-group">
                      <label>
                        Task Description <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={taskForm.description}
                        onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                        placeholder="What needs to be done?"
                        required
                        disabled={!selectedProject}
                      />
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Assign To (Optional)</label>
                        <input
                          type="text"
                          className="form-input"
                          value={taskForm.assignedTo}
                          onChange={(e) => setTaskForm({...taskForm, assignedTo: e.target.value})}
                          placeholder="User ID or email"
                          disabled={!selectedProject}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-select"
                          value={taskForm.status}
                          onChange={(e) => setTaskForm({...taskForm, status: e.target.value})}
                          disabled={!selectedProject}
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="on_hold">On Hold</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label>Priority</label>
                        <select
                          className="form-select"
                          value={taskForm.priority}
                          onChange={(e) => setTaskForm({...taskForm, priority: e.target.value})}
                          disabled={!selectedProject}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="form-actions">
                      <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={!selectedProject || loading.create}
                      >
                        {loading.create ? (
                          <>
                            <span className="spinner"></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <PlusCircle size={16} className="me-1" />
                            Create Task
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="list-section">
                  <div className="list-header">
                    <h3>
                      <ListTask size={20} className="me-2" />
                      Tasks in {getSelectedProjectName()}
                    </h3>
                    <div className="filter-controls">
                      <span className="list-count">{tasks.length} task(s)</span>
                      <div className="priority-filter">
                        <small>Priority: </small>
                        <span className="filter-tag">All</span>
                        <span className="filter-tag">High</span>
                        <span className="filter-tag">Medium</span>
                      </div>
                    </div>
                  </div>
                  {!selectedProject ? (
                    <div className="empty-state">
                      <Check2Square size={48} className="empty-icon" />
                      <p>Please select a project first</p>
                    </div>
                  ) : loading.tasks ? (
                    <div className="loading-state">
                      <span className="spinner"></span>
                      Loading tasks...
                    </div>
                  ) : tasks.length === 0 ? (
                    <div className="empty-state">
                      <Check2Square size={48} className="empty-icon" />
                      <p>No tasks found in this project. Create the first one!</p>
                    </div>
                  ) : (
                    <div className="card-grid compact">
                      {tasks.map(task => (
                        <div key={task.id} className="task-card">
                          <div className="task-header">
                            <div className="task-description">
                              <h4>{task.description}</h4>
                              {task.assigned_to_name && (
                                <small className="assignee">
                                  <Person size={12} className="me-1" />
                                  {task.assigned_to_name}
                                </small>
                              )}
                            </div>
                            <div className="task-meta">
                              <span className={`priority-badge ${task.priority}`}>
                                <Flag size={12} className="me-1" />
                                {task.priority}
                              </span>
                              <span className={`status-badge ${task.status}`}>
                                {task.status.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                          
                          <div className="task-details">
                            <div className="task-info">
                              <span className="info-label">
                                <Calendar size={12} className="me-1" />
                                Created:
                              </span>
                              <span className="info-value">
                                {new Date(task.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="task-info">
                              <span className="info-label">
                                <Clock size={12} className="me-1" />
                                Updated:
                              </span>
                              <span className="info-value">
                                {new Date(task.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    );
};

export default Workspace;